import { createUseStyles } from 'react-jss';
import { objToArr } from './helpers';

const joinParts = (sep = '_', ...args) => args.filter((x) => !!x).join(sep);
const propRule = (prop, value = null) => (value ? `& [_${prop}="${value}"]` : `& [_${prop}]`);


const useStyle = createUseStyles((theme) => {
  const staticDir = (dir) => (dir || '').replace('start', theme.direction.start).replace('end', theme.direction.end) || null;
  const helpers = () => {
    const ret = {};
    // Colors
    [null, 'background', 'text', 'border'].forEach((part) => {
      objToArr(theme.colors, (colorName, colorObject) => {
        const ruleName = propRule(joinParts('_', part, 'color'), colorName);
        switch (part) {
          case 'background':
            ret[ruleName] = {
              background: `${colorObject.normal} !important`,
            };
            break;
          case 'text':
            ret[ruleName] = {
              color: `${colorObject.normal} !important`,
            };
            break;
          case 'border':
            ret[ruleName] = {
              borderColor: `${colorObject.normal} !important`,
            };
            break;
          case null:
          default:
            ret[ruleName] = {
              background: `${colorObject.normal} !important`,
              color: `${colorObject.text} !important`,
              borderColor: `${colorObject.border} !important`,
            };
            break;
        }
      });
    });
    // Positions and Spaces
    ['padding', 'margin'].forEach((name) => {
      [null, 'x', 'y', 'left', 'right', 'top', 'bottom', 'right', 'start', 'end'].forEach((positionName) => {
        ['no', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl'].forEach((sizeName, sizeIndex) => {
          const ruleName = propRule(joinParts('_', name, positionName), sizeName);
          const sizeFactorMap = [0, 0.25, 0.5, 1, 2, 3, 4, 5];
          const propValue = `${theme.sizes.base * sizeFactorMap[sizeIndex]}px !important`;
          if (positionName === 'x') {
            ret[ruleName] = {
              [joinParts('-', name, 'left')]: propValue,
              [joinParts('-', name, 'right')]: propValue,
            };
          } else if (positionName === 'y') {
            ret[ruleName] = {
              [joinParts('-', name, 'top')]: propValue,
              [joinParts('-', name, 'bottom')]: propValue,
            };
          } else {
            const position = staticDir(positionName);
            const propName = joinParts('-', name, position);
            ret[ruleName] = {
              [propName]: propValue,
            };
          }
        });
      });
    });
    // Border
    [null, 'x', 'y', 'left', 'right', 'top', 'bottom', 'right', 'start', 'end'].forEach((positionName) => {
      ['no', 'sm', 'md', 'lg'].forEach((sizeName, sizeIndex) => {
        const ruleName = propRule(joinParts('_', 'border', positionName), sizeName);
        const sizeMap = [0, 1, 1, 1.5];
        const propValue = `${sizeMap[sizeIndex]}px !important`;

        if (positionName === 'x') {
          ret[ruleName] = {
            [joinParts('-', 'border', 'left', 'width')]: propValue,
            [joinParts('-', 'border', 'left', 'style')]: 'solid',
            [joinParts('-', 'border', 'right', 'width')]: propValue,
            [joinParts('-', 'border', 'right', 'style')]: 'solid',
          };
        } else if (positionName === 'y') {
          ret[ruleName] = {
            [joinParts('-', 'border', 'top', 'width')]: propValue,
            [joinParts('-', 'border', 'top', 'style')]: 'solid',
            [joinParts('-', 'border', 'bottom', 'width')]: propValue,
            [joinParts('-', 'border', 'bottom', 'style')]: 'solid',
          };
        } else {
          const position = staticDir(positionName);
          const propName = joinParts('-', 'border', position, 'width');
          const propName2 = joinParts('-', 'border', position, 'style');
          ret[ruleName] = {
            [propName]: propValue,
            [propName2]: 'solid',
          };
        }
      });
    });

    // Shadow
    ['no', 'sm', 'md', 'lg'].forEach((sizeName, sizeIndex) => {
      const ruleName = propRule(joinParts('_', 'shadow'), sizeName);
      if (sizeName === 'no') {
        ret[ruleName] = {
          boxShadow: 'none !important',
        };
      } else {
        const height = theme.sizes.base * [0, 0.1, 0.25, 0.7][sizeIndex];
        const blur = theme.sizes.base * [0, 1.2, 1.5, 1.7][sizeIndex];
        ret[ruleName] = {
          boxShadow: `0 ${height}px ${blur}px 0 ${theme.colors.background.shadow} !important`,
        };
      }
    });

    // Radius
    ['no', 'xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'round'].forEach((sizeName, sizeIndex) => {
      const ruleName = propRule(joinParts('_', 'radius'), sizeName);
      if (sizeName === 'round') {
        ret[ruleName] = {
          borderRadius: '50% !important',
        };
      } else {
        const size = theme.sizes.radius * [0, 0.25, 0.5, 1, 1.5, 3, 4][sizeIndex];
        ret[ruleName] = {
          borderRadius: `${size}px !important`,
        };
      }
    });

    // Size
    ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'].forEach((sizeName, sizeIndex) => {
      const height = theme.sizes.base * [4, 5, 6, 7, 8, 9][sizeIndex];
      const fontSize = theme.sizes.fontBase * [0.6, 0.85, 1, 1.22, 1.45, 1.58][sizeIndex];

      ret[propRule(joinParts('_', 'size'), sizeName)] = {
        height: `${height}px !important`,
        minHeight: `${height}px !important`,
        fontSize: `${fontSize}px !important`,
        lineHeight: '1 !important',
      };
      ret[propRule(joinParts('_', 'text', 'size'), sizeName)] = {
        fontSize: `${fontSize}px !important`,
      };
      ret[propRule(joinParts('_', 'height', 'size'), sizeName)] = {
        height: `${height}px !important`,
        minHeight: `${height}px !important`,
      };
      ret[propRule(joinParts('_', 'radius', 'size'), sizeName)] = {
        borderRadius: `${height}px !important`,
      };
      ret[propRule(joinParts('_', 'width', 'size'), sizeName)] = {
        width: `${height}px !important`,
        minWidth: `${height}px !important`,
      };
    });


    // Displays
    ['block', 'inline', 'inline-block', 'none', 'flex', 'inline-flex'].forEach((value) => {
      ret[propRule(joinParts('_', 'display'), value)] = {
        display: `${value} !important`,
      };
    });


    // Text Weights
    ['bold', 'regular', 'medium', 'light'].forEach((weightName, weightIndex) => {
      const weightMap = ['bold', 'normal', '500', '300'];
      ret[propRule(joinParts('_', 'text', 'weight'), weightName)] = {
        fontWeight: `${weightMap[weightIndex]} !important`,
      };
    });

    // Text Align
    ['center', 'left', 'right', 'start', 'end', 'justify'].forEach((dynamicPos) => {
      const pos = staticDir(dynamicPos);
      ret[propRule(joinParts('_', 'text', 'align'), dynamicPos)] = {
        textAlign: `${pos} !important`,
      };
    });

    // Direction
    ['left', 'right', 'start', 'end'].forEach((dynamicPos) => {
      const pos = staticDir(dynamicPos) === 'left' ? 'ltr' : 'rtl';
      ret[propRule(joinParts('_', 'dir'), dynamicPos)] = {
        direction: `${pos} !important`,
      };
    });

    return ret;
  };


  const grids = (section = 'base', prefix = '') => {
    const width1 = 8.33;
    if (section === 'base') {
      const ret = {};
      if (!prefix) {
        ret[propRule(joinParts('_', 'row'))] = {
          display: 'flex',
          flexWrap: 'wrap',
          width: '100%',
          '& > *': {
            display: 'block',
            flexGrow: 1,
            width: 'auto',
            maxWidth: '100%',
            minHeight: '1px',
            '&[_col_static]': {
              flexGrow: 1,
              flexBasis: 0,
              flexShrink: 1,
            },
          },
        };
      }
      for (let i = 0; i < 12; i += 1) {
        const size = `${width1 * i}%`;
        const name = i.toString();
        ret[propRule(joinParts('_', 'col', prefix), name)] = i === 0 ? {
          display: 'none',
        } : {
          flex: `0 0 ${size} !important`,
          width: `${size} !important`,
          maxWidth: `${size} !important`,
        };
        ret[propRule(joinParts('_', 'col', 'offset', prefix), name)] = {
          [`margin-${theme.direction.start}`]: `${size} !important`,
        };
      }
      ret[propRule(joinParts('_', 'hidden', prefix))] = {
        display: 'none !important',
      };
      return ret;
    } if (section === 'mediaQuery') { // real queries
      const ret = {};
      const mediaNames = ['xs', 'sm', 'md', 'lg', 'xl'];
      const mediaStarts = [0, 480, 768, 992, 1200];
      mediaNames.forEach((mediaName) => {
        const mediaIndex = mediaNames.indexOf(mediaName);
        const prefixName = joinParts('_', mediaName);
        const ruleName = `@media screen and (min-width: ${mediaStarts[mediaIndex]}px)`;
        ret[ruleName] = grids('base', prefixName);
      });
      return ret;
    }
    return null;
  };

  const global = () => ({
    '@global': {
      '*': {
        padding: 0,
        margin: 0,
        border: 0,
        verticalAlign: 'baseline',
        color: 'inherit',
        borderColor: 'inherit',
        backgroundColor: 'transparent',
        '-webkitOverflowScrolling': 'touch',
        '-webkitTapHighlightColor': 'rgba(0, 0, 0, 0)',
        touchAction: 'pan-y',
        boxSizing: 'border-box',
        '&:focus': {
          outline: 'none',
        },
      },
      'a, a:hover, a:visited': {
        textDecoration: 'none',
      },
    },
  });

  return ({
    ...global(),
    themeProvider: (props) => ({
      color: theme.colors.background.text,
      lineHeight: 1.7,
      ...(props.cssHelpers ? {
        ...helpers(),
        ...grids('base'),
        ...grids('mediaQuery'),
      } : {}),
    }),
  });
});

export default useStyle;
